// Auto-Mix: 14 Schritte eines kommerziellen Pop-Mixes. Die Engine misst und schlägt vor,
// jeder Vorschlag hat eine Sicherheit und kann übernommen, angepasst oder verworfen werden.
// Freigeben kann nur ein Mensch. Übernommene Werte landen als Inserts im Projekt
// (srs:* eingebaut oder später bridge:<plugin> = eure eigenen Plugins auf dem Studio-Rechner).
import type { Project, Track, InsertSlot } from '@srs/shared';
import { meterAt, secAfterBeats } from '@srs/shared';
import { engine } from '../audio/engine.ts';
import { uid } from '../state/demo.ts';
import { pluginDef, pluginForRole, resolveParams } from '../audio/plugins.ts';
import { addPoint, points, valueAt } from '../edit/automation.ts';
import { isolate, songEnd } from '../audio/render.ts';
import { defaultBusses } from '../audio/plugins.ts';
import { writeSem, type PluginProfile, type Task } from './pluginmap.ts';
import type { PluginParamInfo } from '@srs/shared';
import { BANDS, guessRole, noisy, integratedLoudness, loudestWindow, measureMix, trackFeatures, type BandId, type MixReport, type Role, type TrackFeatures } from './measure.ts';

export type SuggestionKind = 'mute' | 'gain' | 'volume' | 'pan' | 'hp' | 'peq' | 'shelf' | 'comp' | 'send' | 'busComp' | 'busEq' | 'info';
export type Status = 'pending' | 'accepted' | 'edited' | 'skipped';

export interface Suggestion {
  id: string; step: number; trackId: string | 'bus'; trackName: string;
  label: string; kind: SuggestionKind; value: number; ai: number;
  unit: string; min: number; max: number; stepSize: number;
  f?: number; q?: number; ratio?: number;
  conf: number; note?: string; status: Status;
  /** Rolle der Spur zum Zeitpunkt des Vorschlags (Name, Klang oder vom Menschen gesetzt) */
  tRole?: Role;
  /** Welche Plugin-Rolle das ausführt – wird in der Konsole einem eigenen Plugin zugeordnet */
  role: string;
}

export const STEPS = [
  { t: 'Vorbereitung und Aufräumen', d: 'Phase, Atmer, Klicks und Tuning prüfen, bevor irgendetwas klingt.' },
  { t: 'Gain-Staging', d: 'Alle Spuren auf etwa −18 dBFS RMS, damit jedes Plugin im besten Bereich arbeitet.' },
  { t: 'Balance und Panorama', d: 'Statischer Mix: Lautstärken und Positionen, bevor EQ und Kompression dazukommen.' },
  { t: 'EQ, subtraktiv', d: 'Erst wegnehmen, was stört: Bassanteile, die nur Platz kosten, und Resonanzen.' },
  { t: 'Dynamik', d: 'Kompression pro Spur, damit alles gleichmäßig nach vorn kommt.' },
  { t: 'Maskierung', d: 'Wo sich zwei Spuren im selben Bereich verdecken, bekommt eine Platz.' },
  { t: 'Vocal-Chain', d: 'Die Stimme verkauft den Song. Hier wird am genauesten gearbeitet.' },
  { t: 'Färbung und Sättigung', d: 'Obertöne, damit der Bass auch auf dem Handy hörbar ist.' },
  { t: 'Raum und Tiefe', d: 'Hall und Delay sortieren vorne und hinten. Pop heute: Stimme eher trocken und nah.' },
  { t: 'Stereo und Mono', d: 'Breit, aber monotauglich.' },
  { t: 'Automation', d: 'Bewegung: Stimme reiten, Übergänge betonen.' },
  { t: 'Mix-Bus', d: 'Der Kleber: leichte Bus-Kompression und Farbe über alles.' },
  { t: 'Referenz und Lautheit', d: 'Lautstärkegleicher Vergleich mit einer Referenz.' },
  { t: 'Abhör-Check und Freigabe', d: 'Handy, Mono, Kopfhörer. Freigeben kann nur ein Mensch.' },
] as const;

/** Grobe Rolle einer Spur aus dem Namen. Verrät der Name nichts, schätzt die Analyse aus dem Klang; der Mensch kann jede Rolle im Auto-Mix ändern. */
export function roleOf(t: Track): Role {
  const n = t.name.toLowerCase();
  if (/kick|bd\b/.test(n)) return 'kick';
  if (/snare|sn\b|clap/.test(n)) return 'snare';
  if (/drum|oh\b|overhead|hat|perc|tom|room/.test(n)) return 'drums';
  if (/bass/.test(n)) return 'bass';
  if (/bg|backing|chor/.test(n)) return 'bgv';
  if (/vox|vocal|gesang|stimme|lead ?voc/.test(n)) return 'vox';
  if (/rhodes|keys|piano|klavier|pad|synth|organ|orgel|leslie|wurli|\bb3\b/.test(n)) return 'keys';
  if (/git|guitar|acoustic|akustik/.test(n)) return 'guitar';
  if (/string|streicher|violin|cello/.test(n)) return 'strings';
  if (/hook|lead|melod/.test(n)) return 'lead';
  return 'other';
}

export const ROLE_LABEL: Record<Role, string> = { kick: 'Kick', snare: 'Snare', drums: 'Drums (Overheads, Percussion)', bass: 'Bass', vox: 'Hauptstimme', bgv: 'Backings', keys: 'Keys', guitar: 'Gitarre', strings: 'Streicher', lead: 'Lead-Instrument', other: 'Sonstiges' };

/** Woher die Rolle einer Spur kommt */
export type RoleSource = 'name' | 'klang' | 'mensch';
export function roleFor(t: Track, an: Analysis | undefined, over: Record<string, Role> = {}): { role: Role; src: RoleSource; conf: number } {
  if (over[t.id]) return { role: over[t.id], src: 'mensch', conf: 1 };
  const r = roleOf(t);
  if (r !== 'other' || !an?.tracks[t.id]) return { role: r, src: 'name', conf: r === 'other' ? 0.3 : 0.9 };
  const g = guessRole(an.tracks[t.id]);
  return { role: g.role, src: 'klang', conf: g.conf };
}

/** Ergebnis der Analyse: Merkmale je Spur an der repräsentativen Stelle und die Messung des Ist-Mixes */
export interface Analysis {
  tracks: Record<string, TrackFeatures>;
  /** Analysefenster im Song */
  win: { from: number; len: number; why: string };
  /** Ist-Mix (ganzer Song) vor dem Auto-Mix */
  mix: MixReport;
}

const chans = (b: AudioBuffer) => Array.from({ length: b.numberOfChannels }, (_, c) => b.getChannelData(c));

/**
 * Repräsentative Stelle bestimmen (Refrain-Marker, sonst die lauteste Stelle, dort spielen meist die meisten Instrumente),
 * den Ist-Mix messen und jede Spur dort einzeln ohne Inserts, Fader und Sends rendern und messen.
 */
export async function analyse(p: Project, onStep?: (text: string) => void, len = 15): Promise<Analysis> {
  const end = Math.min(songEnd(p), 600);
  onStep?.('Ist-Mix wird gemessen');
  const full = await engine.bounce(p, end);
  const ch = chans(full);
  const mix = measureMix(ch, full.sampleRate);
  const hook = [...p.markers].sort((a, b) => a.time - b.time).find((m) => /refrain|chorus|hook/i.test(m.name) && m.time < end - 4);
  const from = hook ? hook.time : loudestWindow(ch, full.sampleRate, len);
  const win = { from, len: Math.min(len, end - from), why: hook ? `Marker „${hook.name}“` : 'lauteste Stelle des Songs' };
  const tracks: Record<string, TrackFeatures> = {};
  const list = p.tracks.filter((t) => t.kind !== 'bus' && p.clips.some((c) => c.trackId === t.id));
  for (const [i, t] of list.entries()) {
    onStep?.(`Spur ${i + 1} von ${list.length}: ${t.name}`);
    const q = isolate(p, [t.id], 'freeze', { inserts: false });
    q.tracks[0].pan = 0;
    const buf = await engine.bounce(q, win.len, win.from);
    tracks[t.id] = trackFeatures(chans(buf), buf.sampleRate);
  }
  return { tracks, win, mix };
}

/**
 * Balance in zwei Ebenen, wie Spur → Stem → Mix in der Literatur zum automatischen Mischen:
 * erst die Spuren innerhalb ihrer Gruppe, dann die Gruppen zueinander. Ziele als Lautheit (LU) relativ zur Hauptstimme.
 * Richtwerte für Pop, keine Messergebnisse: der Mensch hört und entscheidet.
 */
export const GROUPS: { id: string; label: string; target: number; members: Partial<Record<Role, number>> }[] = [
  { id: 'vocals', label: 'Hauptstimme', target: 0, members: { vox: 0 } },
  { id: 'bgv', label: 'Backings', target: -7, members: { bgv: 0 } },
  { id: 'drums', label: 'Drums', target: -1.5, members: { kick: 0, snare: 0, drums: -3 } },
  { id: 'bass', label: 'Bass', target: -3, members: { bass: 0 } },
  { id: 'music', label: 'Instrumente', target: -2.5, members: { lead: 0, guitar: -1, keys: -1.5, strings: -2.5, other: -1.5 } },
];
/** Summe aller Spuren nach den Fadern, LUFS: genug Luft für Bus-Kompression und Master, unabhängig davon, welche Gruppen es gibt */
export const MIX_LUFS = -17;

/** Ziel-Lautheit je Spur: Gruppe insgesamt auf ihr Ziel, innerhalb der Gruppe nach Gewicht verteilt (Energiesumme) */
export function balanceTargets(roles: { id: string; role: Role }[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const g of GROUPS) {
    const mem = roles.filter((r) => g.members[r.role] !== undefined);
    if (!mem.length) continue;
    const sum = 10 * Math.log10(mem.reduce((a, r) => a + Math.pow(10, g.members[r.role]! / 10), 0));
    for (const r of mem) out[r.id] = g.target + g.members[r.role]! - sum;
  }
  // Gesamtpegel: alle Ziele gemeinsam verschieben, bis die Summe MIX_LUFS ergibt (das Verhältnis bleibt)
  const ids = Object.keys(out);
  if (ids.length) { const tot = 10 * Math.log10(ids.reduce((a, id) => a + Math.pow(10, out[id] / 10), 0)); for (const id of ids) out[id] += MIX_LUFS - tot; }
  return out;
}

const S = (step: number, t: Track | 'bus', label: string, kind: SuggestionKind, value: number, unit: string, role: string, o: Partial<Suggestion> = {}): Suggestion => ({
  id: uid('am'), step, trackId: t === 'bus' ? 'bus' : t.id, trackName: t === 'bus' ? 'Mix-Bus' : t.name, label, kind, value, ai: value, unit, role,
  min: o.min ?? -24, max: o.max ?? 24, stepSize: o.stepSize ?? 0.5, conf: o.conf ?? 0.85, status: 'pending', ...o,
});

/** Vorschläge für alle 14 Schritte aus Messung + Spurrollen */
export function plan(p: Project, an: Analysis, opts: { roles?: Record<string, Role>; ref?: MixReport; now?: MixReport } = {}): Suggestion[] {
  const out: Suggestion[] = [];
  const tr = p.tracks.filter((t) => t.kind !== 'bus');
  const info = Object.fromEntries(tr.map((t) => [t.id, roleFor(t, an, opts.roles)]));
  const R = (t: Track) => info[t.id].role;
  /** Rolle nur aus dem Klang geschätzt: Vorschläge vorsichtiger */
  const guessed = (t: Track) => info[t.id].src === 'klang';
  const by = (r: Role) => tr.filter((t) => R(t) === r);
  const r1 = (x: number) => Math.round(x * 10) / 10;
  /** Zahl für die Oberfläche: eine Nachkommastelle, deutsches Komma */
  const de = (x: number) => String(r1(x)).replace('.', ',');

  // 0 Vorbereitung: Spuren mit durchgehendem Rauschen melden (nicht automatisch stumm, Sicherheit bewusst niedrig)
  for (const t of tr) {
    const a = an.tracks[t.id];
    if (a && noisy(a)) out.push(S(0, t, `Durchgehendes Rauschen erkannt (Grundpegel ${de(a.floor)} dBFS, ${Math.round(Math.pow(10, a.hf / 10) * 100)} % der Energie über 6 kHz): Spur stummschalten`, 'mute', 1, '', 'utility', { min: 0, max: 1, stepSize: 1, conf: 0.5, note: 'Die Spur klingt wie ein offener Eingang oder ein Masseproblem. Bitte die Aufnahme prüfen; bis dahin stumm, damit Messung und Balance nicht verfälscht werden.' }));
  }
  for (const t of tr.filter((t) => t.kind === 'audio')) out.push(S(0, t, 'Klicks an Schnitten mit Fades glätten', 'info', p.clips.filter((c) => c.trackId === t.id).length, 'Clips', 'utility', { min: 0, max: 50, stepSize: 1, conf: 0.95 }));
  // 1 Gain-Staging auf −18 dBFS RMS, gemessen nur dort, wo die Spur spielt (Pausen ziehen den Wert sonst nach unten)
  const gainOf: Record<string, number> = {};
  for (const t of tr) {
    const a = an.tracks[t.id]; if (!a || a.rmsDb < -80 || a.activity < 0.02) continue;
    const act = a.peakDb - a.crest;
    const g = Math.max(-24, Math.min(24, r1(-18 - act)));
    gainOf[t.id] = g;
    if (Math.abs(g) >= 0.5) out.push(S(1, t, `Eingangspegel angleichen (gemessen ${de(act)} dBFS RMS, solange die Spur spielt)`, 'gain', g, 'dB', 'utility', { conf: 0.95 }));
  }
  // 2 Balance: Ziel-Lautheit je Spur aus Gruppe und Rolle (Spur → Gruppe → Mix), Fader = Ziel − gemessene Lautheit nach dem Gain-Staging
  const targets = balanceTargets(tr.filter((t) => an.tracks[t.id] && Number.isFinite(an.tracks[t.id].lufs) && !noisy(an.tracks[t.id])).map((t) => ({ id: t.id, role: R(t) })));
  for (const t of tr) {
    const a = an.tracks[t.id], d = targets[t.id];
    if (!a || d === undefined) continue;
    const v = Math.max(-24, Math.min(6, Math.round((d - (a.lufs + (gainOf[t.id] ?? 0))) * 2) / 2));
    const g = GROUPS.find((x) => x.members[R(t)] !== undefined)!;
    if (v !== t.volumeDb) out.push(S(2, t, `Fader: ${g.label} auf ${de(d)} LUFS (gemessen ${de(a.lufs + (gainOf[t.id] ?? 0))} LUFS)`, 'volume', v, 'dB', 'utility', { min: -24, max: 6, conf: guessed(t) ? 0.55 : 0.75, note: guessed(t) ? `Rolle aus dem Klang geschätzt (${ROLE_LABEL[R(t)]}). Bitte oben bei den Spurrollen prüfen.` : undefined }));
  }
  by('keys').forEach((t, i) => out.push(S(2, t, 'Keys zur Seite', 'pan', i % 2 ? 0.4 : -0.35, '', 'utility', { min: -1, max: 1, stepSize: 0.05, conf: 0.65 })));
  by('guitar').forEach((t, i) => out.push(S(2, t, 'Gitarre zur Seite', 'pan', i % 2 ? -0.6 : 0.6, '', 'utility', { min: -1, max: 1, stepSize: 0.05, conf: 0.7 })));
  // 3 EQ subtraktiv
  const hp: Partial<Record<Role, number>> = { vox: 85, bgv: 140, keys: 90, guitar: 110, strings: 150, lead: 120, drums: 180, snare: 80 };
  for (const t of tr) { const f = hp[R(t)]; if (f) out.push(S(3, t, `Low-Cut${an.tracks[t.id] ? ` (Bassanteil ${Math.round(an.tracks[t.id].lowRatio * 100)} %)` : ''}`, 'hp', f, 'Hz', 'eq', { min: 20, max: 400, stepSize: 5, conf: an.tracks[t.id]?.lowRatio > 0.25 ? 0.6 : 0.9, note: an.tracks[t.id]?.lowRatio > 0.25 ? 'Viel Energie unter 120 Hz – vielleicht gewollt. Bitte im Kontext anhören.' : undefined })); }
  by('vox').forEach((t) => out.push(S(3, t, 'Mulm bei 340 Hz', 'peq', -2.5, 'dB', 'eq', { f: 340, q: 1.5, min: -12, max: 6 })));
  // 4 Dynamik
  const comp: Partial<Record<Role, [number, number]>> = { kick: [-18, 4], snare: [-16, 3], bass: [-20, 4], vox: [-22, 3], lead: [-20, 2.5] };
  for (const t of tr) { const c = comp[R(t)]; if (c) out.push(S(4, t, `Kompressor ${c[1]}:1, Schwelle`, 'comp', c[0], 'dB', 'comp', { ratio: c[1], min: -40, max: 0 })); }
  // 5 Maskierung
  if (by('kick').length) by('bass').forEach((t) => out.push(S(5, t, 'Platz für die Kick bei 62 Hz', 'peq', -2.5, 'dB', 'eq', { f: 62, q: 1.4, min: -9, max: 0, conf: 0.75 })));
  if (by('vox').length || by('lead').length) [...by('keys'), ...by('guitar')].forEach((t) => out.push(S(5, t, 'Platz für die Stimme bei 2,5 kHz', 'peq', -2, 'dB', 'eq', { f: 2500, q: 1.2, min: -9, max: 3, conf: 0.72 })));
  // 6 Vocal-Chain
  by('vox').forEach((t) => {
    out.push(S(6, t, 'Air bei 10 kHz', 'shelf', 2, 'dB', 'eq', { f: 10000, min: -6, max: 6 }));
    out.push(S(6, t, 'De-Esser bei 6,5 kHz', 'info', -4, 'dB', 'deesser', { min: -10, max: 0, conf: 0.6, note: 'Hängt stark von Text und Stimme ab. Bitte an S-Lauten anhören.' }));
  });
  // 7 Färbung
  by('bass').forEach((t) => out.push(S(7, t, 'Sättigung für kleine Lautsprecher', 'info', 18, '%', 'saturation', { min: 0, max: 40, stepSize: 1 })));
  by('snare').forEach((t) => out.push(S(7, t, 'Knack bei 6 kHz', 'shelf', 1.5, 'dB', 'eq', { f: 6000, min: -6, max: 6 })));
  // 8 Raum
  const rev: Partial<Record<Role, number>> = { vox: 0.14, snare: 0.18, keys: 0.3, strings: 0.38, bgv: 0.42, lead: 0.22 };
  for (const t of tr) { const v = rev[R(t)]; if (v !== undefined) out.push(S(8, t, 'Hall-Anteil', 'send', v, '', 'reverb', { min: 0, max: 0.8, stepSize: 0.02 })); }
  // 9 Stereo / Mono
  [...by('bass'), ...by('kick'), ...by('vox')].forEach((t) => { if (t.pan !== 0) out.push(S(9, t, 'Tiefe und Hauptstimme in die Mitte', 'pan', 0, '', 'utility', { min: -1, max: 1, stepSize: 0.05, conf: 0.95 })); });
  // 10 Automation (Hinweise; das Schreiben der Kurven macht der Mensch)
  by('vox').forEach((t) => out.push(S(10, t, p.markers.some((m) => /refrain|chorus|hook/i.test(m.name)) ? 'Vocal Ride im Refrain (Lautstärke-Automation)' : 'Vocal Rides: erst Refrain-Marker setzen', 'info', 1.5, 'dB', 'utility', { min: 0, max: 4, conf: 0.7 })));
  // 11 Mix-Bus
  out.push(S(11, 'bus', 'Glue-Kompressor 2:1, Schwelle', 'busComp', -12, 'dB', 'buscomp', { ratio: 2, min: -30, max: 0 }));
  // 12 Referenz / Lautheit: mit Referenz aus dem Vergleich der Klangbalance, sonst ein allgemeiner Richtwert
  if (opts.ref) out.push(...refEq(opts.now ?? an.mix, opts.ref));
  else out.push(S(12, 'bus', 'Low-Mid etwas schlanker bei 300 Hz', 'busEq', -1.5, 'dB', 'eq', { f: 300, min: -6, max: 3, conf: 0.5, note: 'Ohne geladene Referenz ist das ein allgemeiner Pop-Richtwert. Mit Referenz kommt der Vorschlag aus der Messung.' }));
  for (const x of out) if (x.trackId !== 'bus') x.tRole = info[x.trackId]?.role;
  return out;
}

/**
 * Balance nachführen: EQ, Kompressor (mit automatischem Make-up) und Sättigung verändern die Lautheit.
 * Jede Spur wird mit ihren Inserts (vor dem Fader) an der Analysestelle neu gemessen und der Fader so gesetzt,
 * dass die Spur wieder auf ihrem Balance-Ziel liegt. In der Literatur heißt es dazu: Mischen ist eine Gesamtaufgabe,
 * jeder Schritt verschiebt die anderen. Deshalb wird nach den Bearbeitungsschritten noch einmal gemessen.
 */
export async function rebalance(p: Project, an: Analysis, roles: Record<string, Role> = {}, onStep?: (t: string) => void): Promise<Suggestion[]> {
  const tr = p.tracks.filter((t) => t.kind !== 'bus' && !t.mute && an.tracks[t.id] && Number.isFinite(an.tracks[t.id].lufs) && !noisy(an.tracks[t.id]));
  const targets = balanceTargets(tr.map((t) => ({ id: t.id, role: roleFor(t, an, roles).role })));
  const out: Suggestion[] = [];
  for (const [i, t] of tr.entries()) {
    onStep?.(`Balance nachmessen: Spur ${i + 1} von ${tr.length}`);
    const q = isolate(p, [t.id], 'freeze', {});
    q.tracks[0].pan = 0;
    const b = await engine.bounce(q, an.win.len, an.win.from);
    const l = integratedLoudness(Array.from({ length: b.numberOfChannels }, (_, c) => b.getChannelData(c)), b.sampleRate);
    const d = targets[t.id];
    if (!Number.isFinite(l) || d === undefined) continue;
    const v = Math.max(-24, Math.min(6, Math.round((d - l) * 2) / 2));
    if (Math.abs(v - t.volumeDb) < 0.5) continue;
    const de = (x: number) => String(Math.round(x * 10) / 10).replace('.', ',');
    const r = roleFor(t, an, roles);
    out.push({ ...S(2, t, `Fader nachführen: nach EQ und Dynamik ${de(l + t.volumeDb)} statt ${de(d)} LUFS`, 'volume', v, 'dB', 'utility', { min: -24, max: 6, conf: r.src === 'klang' ? 0.6 : 0.85 }), tRole: r.role });
  }
  return out;
}

/** Mittenfrequenz eines Bandes (geometrisch) */
const centerOf = (id: BandId) => { const b = BANDS.find((x) => x.id === id)!; return Math.round(Math.sqrt(b.lo * b.hi)); };
/** Master-EQ-Platz je Band: Shelf unten und oben, dazwischen drei Glocken */
const EQ_SLOT: Record<BandId, string> = { sub: 'ls', bass: 'b1', lowmid: 'b2', mid: 'b2', himid: 'b3', pres: 'b3', air: 'hs' };

/**
 * Bus-EQ aus dem Vergleich mit einer Referenz: je Band Unterschied der Energieanteile, halbe Differenz, höchstens ±3 dB,
 * nur ab 1,5 dB Abweichung, ein Band je EQ-Platz (das mit der größten Abweichung).
 */
export function refEq(mix: MixReport, ref: MixReport): Suggestion[] {
  const diffs = BANDS.map((b) => ({ id: b.id, label: b.label, d: ref.bands[b.id] - mix.bands[b.id] })).filter((x) => Math.abs(x.d) >= 1.5 && Number.isFinite(x.d));
  const used = new Set<string>(), out: Suggestion[] = [];
  for (const x of diffs.sort((a, b) => Math.abs(b.d) - Math.abs(a.d))) {
    const slotId = EQ_SLOT[x.id];
    if (used.has(slotId)) continue;
    used.add(slotId);
    const v = Math.max(-3, Math.min(3, Math.round(x.d * 0.5 * 2) / 2));
    if (!v) continue;
    out.push(S(12, 'bus', `${x.label} ${v > 0 ? 'anheben' : 'absenken'} wie in der Referenz (Abweichung ${String(Math.round(x.d * 10) / 10).replace('.', ',')} dB)`, 'busEq', v, 'dB', 'eq', { f: centerOf(x.id), q: 0.9, min: -6, max: 6, conf: 0.6, note: 'Aus dem Vergleich der Klangbalance mit der Referenz, halbe Differenz. Andere Songs klingen anders: bitte lautstärkegleich vergleichen.' }));
  }
  return out;
}

/** Übliche Reihenfolge in einer Kanalkette: Pegel, EQ, Dynamik, De-Esser, Farbe, Raum */
const ORDER = ['gain', 'gate', 'eq', 'comp', 'deesser', 'saturation', 'delay', 'reverb', 'limiter'];
/** Insert für eine Aufgabe holen oder an der passenden Stelle der Kette anlegen. Von der KI gesetzt = ai: true. */
function slot(list: InsertSlot[], role: string): InsertSlot {
  let s = list.find((x) => x.role === role && !x.plugin.startsWith('bridge:'));
  if (!s) {
    const def = pluginForRole(role)!;
    s = { id: uid('ins'), role, plugin: def.id, params: {}, bypass: false };
    const rank = ORDER.indexOf(role);
    const at = list.findIndex((x) => ORDER.indexOf(x.role) > rank);
    if (at < 0) list.push(s); else list.splice(at, 0, s);
  }
  s.ai = true;
  return s;
}
/** Eigene Plugins (Bridge) für Aufgaben, sonst null = eingebautes Plugin */
export interface FxChoice { pick(task: Task, role?: Role): { pluginId: string; label: string; params: PluginParamInfo[]; profile: PluginProfile } | null }

/**
 * Werte für eine Aufgabe in die Kette schreiben: in das gewählte eigene Plugin (über die Reglerzuordnung)
 * oder in das eingebaute. internal = Parameter des eingebauten Plugins, sem = Werte in Einheiten (für eigene Plugins).
 */
function put(list: InsertSlot[], task: Task, internal: Record<string, number>, sem: Record<string, number>, fxc?: FxChoice, role?: Role) {
  const c = fxc?.pick(task, role);
  if (!c) { Object.assign(slot(list, task).params, internal); return; }
  const plugin = `bridge:${c.pluginId}`;
  let s = list.find((x) => x.role === task && x.plugin === plugin);
  if (!s) {
    s = { id: uid('ins'), role: task, plugin, params: {}, bypass: false, label: c.label };
    const own = list.findIndex((x) => x.role === task && !x.plugin.startsWith('bridge:'));
    if (own >= 0) list[own] = s;
    else {
      const rank = ORDER.indexOf(task);
      const at = list.findIndex((x) => ORDER.indexOf(x.role) > rank);
      if (at < 0) list.push(s); else list.splice(at, 0, s);
    }
  }
  s.ai = true;
  const miss = writeSem(s, c.params, c.profile, sem);
  s.unmapped = [...new Set([...(s.unmapped ?? []), ...miss])];
  if (!s.unmapped.length) delete s.unmapped;
}

/** EQ-Band passend zur Frequenz wählen */
const bandFor = (f: number) => (f < 300 ? 'b1' : f < 3000 ? 'b2' : 'b3');
/** Kompressor-Charakter je Spurrolle */
const charFor = (t: Track, r: Role = roleOf(t)) => (r === 'vox' || r === 'bass' || r === 'bgv' ? 1 : r === 'kick' || r === 'snare' || r === 'drums' ? 2 : 0);

/** Übernommene Vorschläge ins Projekt schreiben (eine Undo-Stufe). Alles landet in echten Plugins, die man danach öffnen kann. */
export function applySuggestions(p: Project, list: Suggestion[], fxc?: FxChoice) {
  for (const s of list) {
    if (s.status !== 'accepted' && s.status !== 'edited') continue;
    if (s.trackId === 'bus') {
      p.masterInserts ??= [];
      if (s.kind === 'busComp') Object.assign(slot(p.masterInserts, 'comp').params, { thr: s.value, ratio: s.ratio ?? 2, atk: 30, rel: 300, knee: 6, char: 3, auto: 1, schp: 90 });
      if (s.kind === 'busEq') {
        const f = s.f ?? 300, q = slot(p.masterInserts, 'eq').params;
        if (f <= 60) Object.assign(q, { lsOn: 1, lsF: 80, lsG: s.value });
        else if (f >= 8000) Object.assign(q, { hsOn: 1, hsF: 10000, hsG: s.value });
        else { const b = f < 250 ? 'b1' : f < 2000 ? 'b2' : 'b3'; Object.assign(q, { [`${b}F`]: f, [`${b}G`]: s.value, [`${b}Q`]: s.q ?? 0.8, [`${b}On`]: 1 }); }
      }
      continue;
    }
    const t = p.tracks.find((x) => x.id === s.trackId);
    if (!t) continue;
    const role = s.tRole;
    const eq = (v: Record<string, number>) => put(t.inserts, 'eq', v, v, fxc, role);
    switch (s.kind) {
      case 'gain': put(t.inserts, 'gain', { gain: s.value }, { gain: s.value }, fxc, role); break;
      case 'volume': t.volumeDb = s.value; break;
      case 'mute': t.mute = s.value >= 0.5; break;
      case 'pan': t.pan = s.value; break;
      case 'hp': eq({ hpOn: 1, hpF: s.value, hpSlope: 0 }); break;
      case 'peq': { const b = bandFor(s.f ?? 1000); eq({ [`${b}On`]: 1, [`${b}F`]: s.f ?? 1000, [`${b}Q`]: s.q ?? 1, [`${b}G`]: s.value }); break; }
      case 'shelf': {
        const hi = (s.f ?? 10000) >= 3000;
        eq(hi ? { hsOn: 1, hsF: s.f ?? 10000, hsG: s.value } : { lsOn: 1, lsF: s.f ?? 100, lsG: s.value });
        break;
      }
      case 'comp': {
        const ratio = s.ratio ?? 3;
        // eigenes Plugin: Make-up wie die Auto-Make-up-Formel des eingebauten Kompressors
        put(t.inserts, 'comp', { thr: s.value, ratio, char: charFor(t, s.tRole), auto: 1 }, { thr: s.value, ratio, makeup: Math.round((-(s.value * (1 - 1 / ratio)) / 2) * 10) / 10 }, fxc, role);
        break;
      }
      case 'send': {
        const x = t.sends.find((y) => y.busId === 'reverb');
        if (x) x.level = s.value; else t.sends.push({ busId: 'reverb', level: s.value });
        // Hall-Bus: eigenes Hall-Plugin statt des eingebauten (Bus = 100 % Hall)
        if (fxc?.pick('reverb')) {
          p.busses ??= defaultBusses();
          const bus = p.busses.find((b) => b.id === 'reverb');
          if (bus && !bus.inserts.some((i) => i.plugin === `bridge:${fxc.pick('reverb')!.pluginId}`)) {
            const old = bus.inserts.find((i) => i.role === 'reverb' && !i.plugin.startsWith('bridge:'));
            const decay = old?.params.decay ?? 1.8, pre = old?.params.pre ?? 25;
            put(bus.inserts, 'reverb', {}, { decay, pre, mix: 100 }, fxc);
          }
        }
        break;
      }
      case 'info':
        if (s.role === 'deesser') put(t.inserts, 'deesser', { freq: 6500, thr: -30, range: Math.abs(s.value) }, { freq: 6500, thr: -30, range: Math.abs(s.value) }, fxc, role);
        else if (s.role === 'saturation') put(t.inserts, 'saturation', { type: 0, drive: Math.round((s.value / 3) * 10) / 10 }, { drive: Math.min(1, s.value / 50) }, fxc, role);
        else if (s.step === 10) {
          // Vocal Rides: Stimme in Refrain-Abschnitten anheben, als Lautstärke-Automation (danach frei bearbeitbar)
          const ms = [...p.markers].sort((a, b) => a.time - b.time);
          const hooks = ms.filter((m) => /refrain|chorus|hook/i.test(m.name));
          for (const m of hooks) {
            const end = m.end ?? ms.find((x) => x.time > m.time + 1e-6)?.time ?? secAfterBeats(p, m.time, 8 * meterAt(p, m.time).barLen);
            const base = valueAt(points(t, 'volume'), m.time - 0.3, 'volume', t.volumeDb);
            addPoint(t, 'volume', Math.max(0, m.time - 0.3), base);
            addPoint(t, 'volume', m.time, base + s.value);
            addPoint(t, 'volume', end, base + s.value);
            addPoint(t, 'volume', end + 0.3, base);
          }
          if (hooks.length) t.automation!.mode = 'read';
        }
        else if (s.step === 0) for (const c of p.clips) if (c.trackId === t.id && c.kind === 'audio') { c.fadeIn = Math.max(c.fadeIn, 0.005); c.fadeOut = Math.max(c.fadeOut, 0.01); }
        break;
    }
  }
}

/** aktueller Wert eines Inserts nach Rolle, mit Standardwerten des Plugins */
export function slotParams(t: Track, role: string) {
  const s = t.inserts.find((x) => x.role === role && !x.bypass);
  const def = s && pluginDef(s.plugin);
  return def ? resolveParams(def, s.params) : undefined;
}

export const currentValue = (t: Track, s: Suggestion) => {
  switch (s.kind) {
    case 'volume': return t.volumeDb;
    case 'pan': return t.pan;
    case 'hp': { const q = slotParams(t, 'eq'); return q?.hpOn ? q.hpF : 20; }
    case 'gain': return slotParams(t, 'gain')?.gain ?? 0;
    default: return undefined;
  }
};
